import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { BulkDrawerFormFieldsMap } from 'UI/components/organisms/BulkEmailForm/fields';

import { useAIGenerator } from '../BulkEmailDrawerAI.hooks';

/**
 * Owns the AI-generator logic for the bulk email flows: watches the form fields the generator
 * needs, wires them into `useAIGenerator`, and exposes the button state plus a memoized bundle of
 * props for `<BulkAIGeneratorDrawer />`. The button and the drawer are rendered by the consumer in
 * their respective tree locations.
 *
 * @param {Object} params
 * @param {string} [params.emailBodyKey]
 * @param {(content: { promptId: string, emailBody: string, subject: string }) => void} params.onSetEmailContent
 */
export const useBulkAIGenerator = ({
  emailBodyKey = BulkDrawerFormFieldsMap.EmailBody.key,
  onSetEmailContent
}) => {
  const { watch } = useFormContext();
  const formDataForAiGenerator = watch([
    BulkDrawerFormFieldsMap.To.key,
    BulkDrawerFormFieldsMap.Template.key,
    BulkDrawerFormFieldsMap.BulkScope.key,
    BulkDrawerFormFieldsMap.MarketCandidates.key,
    BulkDrawerFormFieldsMap.JobOrderRecruitment.key,
    BulkDrawerFormFieldsMap.Attempts.key
  ]);

  const currentEmailBody = watch(emailBodyKey);

  const {
    buttonDefinitions,
    entity,
    entityResult,
    handleClickRateButton,
    handleCloseAIDrawer,
    handleGenerateWithAi,
    handleOpenAIDrawer,
    isAIDrawerOpen,
    pluralEntity,
    shouldEnableAIAction,
    shouldShowAIButton
  } = useAIGenerator({
    bulkData: {
      ...formDataForAiGenerator,
      currentEmailBody
    },
    onUseThisEmail: onSetEmailContent
  });

  const drawerProps = useMemo(
    () => ({
      buttonDefinitions,
      entity,
      entityResult,
      pluralEntity,
      searchProject: formDataForAiGenerator.to,
      onClickGenerate: handleGenerateWithAi,
      onClickRateButton: handleClickRateButton,
      onClose: handleCloseAIDrawer
    }),
    [
      buttonDefinitions,
      entity,
      entityResult,
      pluralEntity,
      formDataForAiGenerator.to,
      handleGenerateWithAi,
      handleClickRateButton,
      handleCloseAIDrawer
    ]
  );

  return {
    isAIDrawerOpen,
    shouldShowAIButton,
    shouldEnableAIAction,
    handleOpenAIDrawer,
    drawerProps
  };
};
